import { useMemo, useRef, useState } from "react"
import { useSelector } from 'react-redux';

import GoogleMapReact from "google-map-react"
import Supercluster from "supercluster"
import ClusterMarker from "./ClusterMarker"
import LocationMarker from "./LocationMarker"
import LocationInfoBox from "./LocationInfoBox"
import genericMarker from "./generic-marker.svg"

import _ from 'lodash';

import { useGetRegionsQuery } from "../store/atfRegionApiSlice";
import { useGetCategoriesQuery } from "../store/atfCategoryApiSlice";
import { useGetPOIsQuery } from "../store/atfPoiApiSlice";
import { selectParentCategories } from '../store/selectors';

const Map = () => {

    const { data: regions = [], isLoading: regionsFetching } = useGetRegionsQuery();
    const { data: categories = [], isFetching: categoriesFetching } = useGetCategoriesQuery();
    const { data: entities = [], isFetching: poisFetching } = useGetPOIsQuery();

    const currentState = useSelector((state) => state.currentState.value)
    const currentRegion = useSelector((state) => state.currentRegion.value)
    const selectedPoiTypes = useSelector((state) => state.poiTypes.value)
    const parentCategories = useSelector(selectParentCategories);

    const [locationInfo, setLocationInfo] = useState(null)
    const [showLocationInfoBox, setShowLocationInfoBox] = useState(false)
    const [mapView, setMapView] = useState({bounds: [112, -44, 154, -10], zoom: 5})
    const mapRef = useRef(null)

    const getZoom = () => {
        if(currentRegion.id){
            return 10;
        } else if (currentState.id){
            return 7;
        } else {
            return 5;
        }
    }

    const getCenter = () => {
        if(currentRegion.id){
            return {
                lat: currentRegion.meta.atf_latitude,
                lng: currentRegion.meta.atf_longitude
            }
        } else if (currentState.id){
            return {
                lat: currentState.meta.atf_latitude,
                lng: currentState.meta.atf_longitude
            }
        } else {
            return {
                lat: -26.5, // -32.2569,
                lng: 133.25 // 148.6011
            };
        }
    }

    const getEntityIcon = (entity) => {
        const category = getEntityCategory(entity); 
        return category?.meta?.atf_icon || genericMarker;
    }

    const getEntityCategory = (entity) => {
        if ((entity.atf_category ?? []).length < 1){
            return undefined;
        } else {
            const childCategories = _.difference(entity.atf_category, parentCategories);
            const categoryId = _.isEmpty(childCategories) ? entity.atf_category[0] : childCategories[0];
            return _.find(categories, category => {
                return category.id === categoryId
            })
        }
    }

    const onMapIconClick = (entity) => {
        setLocationInfo(entity);
        setShowLocationInfoBox(true);
    }

    const points = useMemo(() => {
        return _.chain(entities)
        .filter(entity => {
            if(currentRegion.id){
                return entity.meta?.atf_parent_region === currentRegion.id;
            } else if(currentState.id){
                const regionId = entity.meta?.atf_parent_region;
                if(regionId){
                    const region = _.find(regions, region => { return region.id === regionId })
                    return (region && region.atf_state) ? region.atf_state[0] === currentState.id : false;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        }).filter(entity => {
            if ((entity.atf_category ?? []).length < 1){
                return false;
            } else {
                return _.every(entity.atf_category, c => {
                    return !_.has(selectedPoiTypes, c) || selectedPoiTypes[c];
                })
            }
        }).value()
    }, [entities, currentRegion.id, currentState.id, regions, selectedPoiTypes])

    const clusterPoints = useMemo(() => points.map(entity => ({
        type: 'Feature',
        properties: {entity},
        geometry: {
            type: 'Point',
            coordinates: [Number(entity.meta.atf_longitude), Number(entity.meta.atf_latitude)],
        },
    })).filter(point => point.geometry.coordinates.every(Number.isFinite)), [points])

    const clusterIndex = useMemo(() => {
        return new Supercluster({radius: 60, maxZoom: 17}).load(clusterPoints)
    }, [clusterPoints])

    const clusters = useMemo(() => {
        return clusterIndex.getClusters(mapView.bounds, Math.floor(mapView.zoom))
    }, [clusterIndex, mapView])

    const onMapChange = ({bounds, zoom}) => {
        setMapView({
            bounds: [bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat],
            zoom,
        })
    }

    const onClusterClick = cluster => {
        const [lng, lat] = cluster.geometry.coordinates;
        mapRef.current?.panTo({lat, lng});
        mapRef.current?.setZoom(Math.min(clusterIndex.getClusterExpansionZoom(cluster.id), 18));
    }


    const defaultMapOptions = {
        fullscreenControl: false,
    };

    return (
        <div className="map">

            <GoogleMapReact
                bootstrapURLKeys={{ 
                    key: 'AIzaSyBr5bbagqBVo3NNL_1vyBhlDhEeoJ5vq3Y' 
                }}
                center={getCenter()}
                zoom={getZoom()}
                defaultOptions={defaultMapOptions}
                onChange={onMapChange}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({map}) => { mapRef.current = map; }}
            >
                {clusters.map(cluster => {
                    const [lng, lat] = cluster.geometry.coordinates;
                    if(cluster.properties.cluster){
                        return <ClusterMarker
                            key={`cluster-${cluster.id}`}
                            count={cluster.properties.point_count}
                            lat={lat}
                            lng={lng}
                            onClick={() => onClusterClick(cluster)} />
                    }

                    const entity = cluster.properties.entity;
                    return <LocationMarker
                        key={entity.id}
                        mapIcon={getEntityIcon(entity)}
                        lat={lat}
                        lng={lng}
                        onClick={() => onMapIconClick(entity)} />
                })}
            </GoogleMapReact>

            {locationInfo && showLocationInfoBox && 
                <>
                <LocationInfoBox 
                    setShowLocationInfoBox={setShowLocationInfoBox} 
                    entity={locationInfo} 
                    entities={points}
                    category={getEntityCategory(locationInfo)}
                    categories={categories}
                ></LocationInfoBox>
                
                </> 
            }

        </div>
    )
}

export default Map
